// @ts-nocheck
/**
 * Auth API Transformers
 * Handles conversion between Strapi DTOs and Domain models
 */

import { normalizeSaudiPhoneForStorage } from '@spcc/core'
import type { 
  AuthUser, 
  LoginCredentials, 
  RegisterData,
  ForgotPasswordRequest,
  ForgotPasswordResult,
  ResetPasswordRequest,
  ResetPasswordResult,
  ChangePasswordRequest,
  ChangePasswordResult,
  ChangePasswordOtpRequest,
  ChangePasswordOtpResult,
  DeleteAccountRequest,
  DeleteAccountResult,
  UpdateProfileRequest,
  UpdateProfileResult,
} from '../domain/types'
import type { 
  UserDto, 
  LoginRequestDto, 
  AuthResponseDto,
  ForgotPasswordRequestDto,
  ForgotPasswordResponseDto,
  ResetPasswordRequestDto,
  ResetPasswordResponseDto,
  ChangePasswordRequestDto,
  ChangePasswordOtpRequestDto,
  ChangePasswordResponseDto,
  RequestChangePasswordOtpRequestDto,
  RequestChangePasswordOtpResponseDto,
  DeleteAccountRequestDto,
  DeleteAccountResponseDto,
  UpdateProfileRequestDto,
  UpdateProfileResponseDto,
} from './api-types'

// ============================================================================
// DTO → Domain Transformers
// ============================================================================

/**
 * Transform Strapi UserDto to Domain AuthUser
 */
export function transformUserDtoToAuthUser(dto: UserDto): AuthUser {
  return {
    id: dto.id,
    documentId: dto.documentId ?? String(dto.id),
    username: dto.username,
    email: dto.email,
    confirmed: dto.confirmed,
    blocked: dto.blocked,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    provider: dto.provider,
    fullName: dto.fullName,
    phone: dto.phone,
    companyName: dto.companyName,
    tenant: dto.tenant
      ? {
          id: dto.tenant.id,
          documentId: dto.tenant.documentId,
          name: dto.tenant.name,
          code: dto.tenant.code,
          tenantStatus: dto.tenant.tenantStatus,
          channel: dto.tenant.channel,
          requireSubCustomer: dto.tenant.requireSubCustomer,
          allowPortalQueueJoin: dto.tenant.allowPortalQueueJoin,
        }
      : undefined,
    tenantId: dto.tenant?.id ? String(dto.tenant.id) : undefined,
    role: dto.role
      ? {
          id: dto.role.id,
          documentId: dto.role.documentId,
          name: dto.role.name,
          description: dto.role.description,
          type: dto.role.type,
        }
      : undefined,
    factoryLocation: dto.factoryLocation ?? null,
    userLocation: dto.userLocation ?? null,
    factoryDocumentId: dto.factoryDocumentId ?? null,
  }
}

/**
 * Transform auth response to domain format
 */
export function transformAuthResponse(response: AuthResponseDto): {
  token: string
  user: AuthUser
} {
  return {
    token: response.jwt,
    user: transformUserDtoToAuthUser(response.user),
  }
}

/**
 * Transform forgot password response to domain format
 */
export function transformForgotPasswordResponse(response: ForgotPasswordResponseDto): ForgotPasswordResult {
  return {
    ok: response.ok,
    message: response.message || 'Password reset email sent',
  }
}

/**
 * Transform reset password response to domain format
 */
export function transformResetPasswordResponse(response: ResetPasswordResponseDto): ResetPasswordResult {
  const result: ResetPasswordResult = {
    message: response.message || 'Password reset successful',
  }
  
  if (response.jwt && response.user) {
    result.jwt = response.jwt
    result.user = transformUserDtoToAuthUser(response.user)
  }
  
  return result
}

/**
 * Transform change password response to domain format
 */
export function transformChangePasswordResponse(response: ChangePasswordResponseDto): ChangePasswordResult {
  return {
    jwt: response.jwt,
    user: transformUserDtoToAuthUser(response.user),
    message: response.message ?? '',
  }
}

export function transformChangePasswordOtpResponse(
  response: RequestChangePasswordOtpResponseDto
): ChangePasswordOtpResult {
  return {
    ok: response.ok,
    channel: response.channel,
    message:
      response.message ??
      (response.channel === 'email'
        ? 'Verification code sent to your email address.'
        : 'Verification code sent to your phone number.'),
  }
}

/**
 * Transform delete account response to domain format
 */
export function transformDeleteAccountResponse(response: DeleteAccountResponseDto): DeleteAccountResult {
  return {
    ok: response.ok,
    message: response.message || 'Account deleted successfully',
  }
}

/**
 * Transform update profile response to domain format
 */
export function transformUpdateProfileResponse(response: UpdateProfileResponseDto): UpdateProfileResult {
  return {
    user: transformUserDtoToAuthUser(response),
  }
}

// ============================================================================
// Domain → DTO Transformers
// ============================================================================

/**
 * Transform login credentials to API request format
 */
export function transformLoginCredentials(credentials: LoginCredentials): LoginRequestDto {
  return {
    identifier: credentials.identifier,
    password: credentials.password,
    ...(credentials.turnstileToken && { turnstileToken: credentials.turnstileToken }),
  }
}

/**
 * Transform register data to API request format
 */
export function transformRegisterData(data: RegisterData): FormData {
  const formData = new FormData()

  formData.append('username', data.username)
  formData.append('email', data.email)
  formData.append('password', data.password)
  formData.append('fullName', data.fullName)
  formData.append('companyName', data.companyName)
  formData.append('mobilePhone', normalizeSaudiPhoneForStorage(data.mobilePhone) ?? data.mobilePhone)
  if (data.telephone) formData.append('telephone', normalizeSaudiPhoneForStorage(data.telephone) ?? data.telephone)
  formData.append('vatNumber', data.vatNumber)
  formData.append('crNumber', data.crNumber)
  formData.append('addressLine1', data.addressLine1)
  if (data.houseNumber) formData.append('houseNumber', data.houseNumber)
  formData.append('city', data.city ?? '')
  formData.append('region', data.region ?? '')
  formData.append('postalCode', data.postalCode ?? '')
  if (data.country) formData.append('country', data.country)
  formData.append('sapCustomerGroup', data.sapCustomerGroup)

  formData.append('vatCertificate', data.vatCertificate, data.vatCertificate.name)
  formData.append('crDocument', data.crDocument, data.crDocument.name)

  return formData
}

/**
 * Transform forgot password request to API format
 */
export function transformForgotPasswordRequest(data: ForgotPasswordRequest): ForgotPasswordRequestDto {
  return {
    email: data.email,
  }
}

/**
 * Transform reset password request to API format
 */
export function transformResetPasswordRequest(data: ResetPasswordRequest): ResetPasswordRequestDto {
  return {
    code: data.code,
    password: data.password,
    passwordConfirmation: data.passwordConfirmation,
  }
}

/**
 * Transform change password request to API format
 */
export function transformChangePasswordRequest(
  data: ChangePasswordRequest
): ChangePasswordRequestDto | ChangePasswordOtpRequestDto {
  if (data.verificationMethod === 'current_password') {
    return {
      currentPassword: data.currentPassword,
      password: data.password,
      passwordConfirmation: data.passwordConfirmation,
    }
  }

  const channel = data.verificationMethod === 'email_otp' ? 'email' : 'phone'
  return {
    channel,
    code: data.otpCode,
    password: data.password,
    passwordConfirmation: data.passwordConfirmation,
  }
}

export function transformChangePasswordOtpRequest(
  data: ChangePasswordOtpRequest
): RequestChangePasswordOtpRequestDto {
  return {
    channel: data.channel,
  }
}

/**
 * Transform delete account request to API format
 */
export function transformDeleteAccountRequest(data: DeleteAccountRequest): DeleteAccountRequestDto {
  return {
    deletionReason: data.deletionReason,
  }
}

/**
 * Transform update profile request to API format
 */
export function transformUpdateProfileRequest(data: UpdateProfileRequest): UpdateProfileRequestDto {
  return {
    companyName: data.companyName.trim(),
    phone: data.phone.trim(),
  }
}
